import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  AccountingTransaction,
  annotateTransaction,
  applyGroupDefaults,
  assignActNumbers,
  clearError,
  clearKudirSummary,
  exportKudirCsv,
  exportTransactionsCsv,
  fetchAccountingTransactions,
  fetchKudirSummary,
  runPaymentDocMatching,
  setFilters,
  setPage,
  setTransactionStatus,
  uploadAcquiringReport,
  uploadStatement,
} from '../store/slices/accountingSlice';
import AsyncSearchSelect, { SelectOption } from './AsyncSearchSelect';
import Select from './Select';
import ConfirmationModal from './ConfirmationModal';
import LoadingSpinner from './LoadingSpinner';
import './Accounting.css';

// Single-page quarterly accounting workflow (docs/specs/accounting-ingest-and-annotation.md):
// upload the two raw bank files, annotate transactions (the former 4 manual sheet
// columns), run the ПП matcher. Owner-only page by convention — kept terse.

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

interface StudentOption extends SelectOption<number> {
  student: { id: number; firstName: string; lastName: string; surname?: string | null };
}

interface Draft {
  studentOption: StudentOption | null;
  type: 'group' | 'individual' | null;
  lessons: string;
  price: string;
}

const emptyDraft: Draft = { studentOption: null, type: null, lessons: '', price: '' };

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const [y, m, d] = value.slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
}

function studentLabel(student: { firstName: string; lastName: string; surname?: string | null }): string {
  return [student.lastName, student.firstName, student.surname].filter(Boolean).join(' ');
}

// Group thousands with a plain space (12345 -> "12 345") — matches the КУДИР book's integers.
function formatMoney(n: number): string {
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

// The КУДИР period selector holds either a full year ("2025") or a single quarter ("2025-Q2").
// One control, one selection — the list, totals and every export follow whichever is picked.
const isYearPeriod = (p: string): boolean => /^\d{4}$/.test(p);
const periodScope = (p: string): { year: number } | { quarter: string } =>
  isYearPeriod(p) ? { year: Number(p) } : { quarter: p };
const periodLabel = (p: string): string => (isYearPeriod(p) ? `весь ${p}` : p.replace('-', ' '));

function amountsMismatch(lessons: number, price: number, amount: string): boolean {
  if (!Number.isFinite(lessons) || !Number.isFinite(price) || lessons <= 0 || price <= 0) return false;
  return Math.round(lessons * price * 100) !== Math.round(parseFloat(amount) * 100);
}

function UploadZone({ label, hint, uploading, onFile }: {
  label: string;
  hint: string;
  uploading: boolean;
  onFile: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  return (
    <div
      className={`accounting-upload-zone ${dragOver ? 'is-dragover' : ''}`}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) onFile(file);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
          e.target.value = '';
        }}
      />
      <div className="accounting-upload-zone__label">{label}</div>
      <div className="accounting-upload-zone__hint">{uploading ? 'Загрузка…' : hint}</div>
    </div>
  );
}

function Accounting() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const token = useAppSelector((state) => state.auth.token);
  const {
    transactions, pagination, filters, loading, error,
    acquiringSummary, statementSummary, uploadingAcquiring, uploadingStatement,
    matchStats, matching, savingIds, bulkBusy, exporting, kudirSummary,
  } = useAppSelector((state) => state.accounting);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [showMatchConfirm, setShowMatchConfirm] = useState(false);
  const [showAssignActs, setShowAssignActs] = useState(false);
  const [assignStart, setAssignStart] = useState('');
  const [pendingStatusTx, setPendingStatusTx] = useState<AccountingTransaction | null>(null);
  const [bulkNotice, setBulkNotice] = useState<string | null>(null);
  // filters.quarter holds a КУДИР period — a whole year ("2025") or a single quarter ("2025-Q2").
  const { quarter: period, annotated, matched } = filters;

  const fetchParams = useMemo(() => ({
    page: pagination.currentPage,
    limit: pagination.itemsPerPage,
    ...(period ? periodScope(period) : {}),
    annotated,
    matched,
  }), [pagination.currentPage, pagination.itemsPerPage, period, annotated, matched]);

  useEffect(() => {
    dispatch(fetchAccountingTransactions(fetchParams));
  }, [dispatch, fetchParams]);

  // КУДИР totals (act-date axis) load whenever a period — a quarter or a whole year — is
  // selected, and clear when it is not; the list buckets by the same period.
  useEffect(() => {
    if (period) dispatch(fetchKudirSummary(periodScope(period)));
    else dispatch(clearKudirSummary());
  }, [dispatch, period]);

  const refetch = useCallback(() => {
    dispatch(fetchAccountingTransactions(fetchParams));
    if (period) dispatch(fetchKudirSummary(periodScope(period)));
  }, [dispatch, fetchParams, period]);

  // КУДИР history starts 2024 (earliest filed act) and the ceiling tracks the current quarter.
  // Newest first, each year block led by its «весь YYYY» full-year option, then that year's
  // quarters (the current year stops at the current quarter). Selecting the year exports/lists
  // the whole year; selecting a quarter, just that quarter.
  const periodOptions = useMemo(() => {
    const FIRST_YEAR = 2024;
    const now = new Date();
    const curYear = now.getFullYear();
    const curQ = Math.floor(now.getMonth() / 3) + 1;
    const options: SelectOption<string>[] = [];
    for (let year = curYear; year >= FIRST_YEAR; year -= 1) {
      options.push({ value: `${year}`, label: `весь ${year}` });
      const startQ = year === curYear ? curQ : 4;
      for (let q = startQ; q >= 1; q -= 1) {
        options.push({ value: `${year}-Q${q}`, label: `${year} Q${q}` });
      }
    }
    return options;
  }, []);

  const loadStudentOptions = useCallback(async (search: string): Promise<StudentOption[]> => {
    if (!token) return [];
    const response = await axios.get(`${API_BASE_URL}/students`, {
      params: { search, limit: 50 },
      headers: { Authorization: `Bearer ${token}` },
    });
    return (response.data.students as StudentOption['student'][]).map((s) => ({
      value: s.id,
      label: studentLabel(s),
      student: s,
    }));
  }, [token]);

  const startEdit = (tx: AccountingTransaction) => {
    setEditingId(tx.id);
    setDraft({
      studentOption: tx.student
        ? { value: tx.student.id, label: studentLabel(tx.student), student: tx.student }
        : null,
      type: tx.type ?? null,
      lessons: tx.lessonsCount != null ? String(tx.lessonsCount) : '',
      price: tx.unitPrice != null ? String(tx.unitPrice) : '',
    });
  };

  const draftLessons = parseInt(draft.lessons, 10);
  const draftPrice = parseFloat(draft.price);
  const draftValid = !!draft.studentOption && !!draft.type
    && Number.isInteger(draftLessons) && draftLessons >= 1
    && Number.isFinite(draftPrice) && draftPrice > 0;

  const handleSave = async () => {
    if (editingId == null || !draftValid || !draft.studentOption || !draft.type) return;
    try {
      await dispatch(annotateTransaction({
        id: editingId,
        studentId: draft.studentOption.value,
        type: draft.type,
        lessonsCount: draftLessons,
        unitPrice: draftPrice,
      })).unwrap();
      setEditingId(null);
      setDraft(emptyDraft);
      // Annotation propagates the student to same-card rows server-side; refetch so
      // those siblings show their newly linked student.
      refetch();
    } catch {
      // error surfaced via slice state
    }
  };

  const handleAcquiringFile = (file: File) => {
    dispatch(uploadAcquiringReport(file)).finally(refetch);
  };

  const handleStatementFile = (file: File) => {
    dispatch(uploadStatement(file)).finally(refetch);
  };

  const handleRunMatching = async () => {
    setShowMatchConfirm(false);
    try {
      await dispatch(runPaymentDocMatching()).unwrap();
      refetch();
    } catch {
      // error surfaced via slice state
    }
  };

  const handleStatusConfirm = async () => {
    if (!pendingStatusTx) return;
    const next = pendingStatusTx.status === 'paid' ? 'returned' : 'paid';
    try {
      await dispatch(setTransactionStatus({ id: pendingStatusTx.id, status: next })).unwrap();
      // A returned row leaves the КУДИР totals — refresh them for the selected period.
      if (period) dispatch(fetchKudirSummary(periodScope(period)));
    } finally {
      setPendingStatusTx(null);
    }
  };

  const bulkFilters = useMemo(() => ({ quarter: period, matched }), [period, matched]);

  const handleFillDefaults = async () => {
    setBulkNotice(null);
    try {
      const result = await dispatch(applyGroupDefaults(bulkFilters)).unwrap();
      setBulkNotice(`Заполнено: ${result.filled}${result.skippedIrregular ? ` · нестандартных пропущено: ${result.skippedIrregular}` : ''}`);
      refetch();
    } catch {
      // error surfaced via slice state
    }
  };

  const handleExport = (scope: 'period' | 'year') => {
    if (scope === 'year') {
      const year = period ? parseInt(period.slice(0, 4), 10) : new Date().getFullYear();
      dispatch(exportTransactionsCsv({ year, matched }));
    } else {
      dispatch(exportTransactionsCsv({ ...(period ? periodScope(period) : {}), matched }));
    }
  };

  const handleAssignActs = async () => {
    setShowAssignActs(false);
    setBulkNotice(null);
    try {
      const startNumber = assignStart.trim() ? parseInt(assignStart.trim(), 10) : undefined;
      const result = await dispatch(assignActNumbers({ ...bulkFilters, startNumber })).unwrap();
      setAssignStart('');
      const parts = [`Присвоено номеров: ${result.assigned}`];
      if (result.assigned > 0) parts.push(`№${result.startNumber}–${result.lastNumber}`);
      if (result.skippedExisting) parts.push(`уже с актом: ${result.skippedExisting}`);
      if (result.skippedUnready) parts.push(`не готово: ${result.skippedUnready}`);
      setBulkNotice(parts.join(' · '));
      refetch();
    } catch {
      // error surfaced via slice state
    }
  };

  const handleKudirExport = () => {
    if (!period) return;
    dispatch(exportKudirCsv(periodScope(period)));
  };

  const renderRow = (tx: AccountingTransaction) => {
    const isEditing = editingId === tx.id;
    const saving = savingIds.includes(tx.id);
    const rowMismatch = !isEditing && tx.lessonsCount != null && tx.unitPrice != null
      && amountsMismatch(tx.lessonsCount, tx.unitPrice, tx.amount);
    const editMismatch = isEditing && amountsMismatch(draftLessons, draftPrice, tx.amount);
    // "Готово" = the row has everything an act needs (the data itself is the readiness
    // signal; there is no separate confirm step).
    const isComplete = tx.studentId != null && tx.type != null && tx.lessonsCount != null && tx.unitPrice != null;

    return (
      <tr key={tx.id} className={tx.status === 'returned' ? 'accounting-row--returned' : ''}>
        <td>{formatDate(tx.paymentDate)}</td>
        <td><strong>{tx.amount}</strong> {tx.currency}</td>
        <td>
          {tx.cardHolderName || <span className="accounting-muted">—</span>}
          {tx.cardNumber && <span className="accounting-card-number">{tx.cardNumber}</span>}
        </td>
        {isEditing ? (
          <>
            <td>
              <div className="accounting-student-select">
                <AsyncSearchSelect<number, StudentOption>
                  value={draft.studentOption}
                  onChange={(option) => setDraft((d) => ({ ...d, studentOption: option }))}
                  loadOptions={loadStudentOptions}
                  placeholder="Поиск студента…"
                  noOptionsMessage="Студенты не найдены"
                />
              </div>
            </td>
            <td>
              <div className="accounting-segments accounting-segments--compact" role="group" aria-label="Формат уроков">
                <button
                  type="button"
                  className={`accounting-segments__btn ${draft.type === 'group' ? 'is-active' : ''}`}
                  onClick={() => setDraft((d) => ({ ...d, type: 'group' }))}
                >г</button>
                <button
                  type="button"
                  className={`accounting-segments__btn ${draft.type === 'individual' ? 'is-active' : ''}`}
                  onClick={() => setDraft((d) => ({ ...d, type: 'individual' }))}
                >и</button>
              </div>
            </td>
            <td>
              <input
                type="number"
                min={1}
                className="form-input accounting-num"
                aria-label="Кол-во уроков"
                value={draft.lessons}
                onChange={(e) => setDraft((d) => ({ ...d, lessons: e.target.value }))}
              />
            </td>
            <td>
              <input
                type="number"
                min={0}
                step={0.01}
                className="form-input accounting-num"
                aria-label="Цена за урок"
                value={draft.price}
                onChange={(e) => setDraft((d) => ({ ...d, price: e.target.value }))}
              />
              {editMismatch && <span className="acc-badge acc-badge--warning" title="Уроки × цена не равны сумме платежа">Σ ≠ сумме</span>}
            </td>
          </>
        ) : (
          <>
            <td>{tx.student ? studentLabel(tx.student) : <span className="accounting-muted">—</span>}</td>
            <td>{tx.type ? (tx.type === 'group' ? 'г' : 'и') : <span className="accounting-muted">—</span>}</td>
            <td>{tx.lessonsCount ?? <span className="accounting-muted">—</span>}</td>
            <td>
              {tx.unitPrice ?? <span className="accounting-muted">—</span>}
              {rowMismatch && <span className="acc-badge acc-badge--warning" title="Уроки × цена не равны сумме платежа"> Σ ≠ сумме</span>}
            </td>
          </>
        )}
        <td>
          {tx.bankPaymentDocument
            ? <>№{tx.bankPaymentDocument}<span className="accounting-card-number">{formatDate(tx.bankPaymentDocumentDate)}</span></>
            : <span className="accounting-muted">—</span>}
        </td>
        <td>
          {tx.status === 'returned'
            ? <span className="acc-badge acc-badge--returned">Возврат</span>
            : isComplete
              ? <span className="acc-badge acc-badge--ok">Готово</span>
              : <span className="accounting-muted">—</span>}
        </td>
        <td>
          {tx.certificate ? (
            <span className="accounting-act" title={tx.certificate.generatedAt ? 'PDF сгенерирован' : 'PDF ещё не сгенерирован'}>
              №{tx.certificate.certNumber}
              <span className={`accounting-act-dot ${tx.certificate.generatedAt ? 'is-done' : 'is-pending'}`} />
            </span>
          ) : (
            <span className="accounting-muted">—</span>
          )}
        </td>
        <td>
          {isEditing ? (
            <div className="accounting-edit-actions">
              <button className="btn-success" onClick={handleSave} disabled={!draftValid || saving}>
                {saving ? '…' : 'Save'}
              </button>
              <button className="btn-secondary" onClick={() => { setEditingId(null); setDraft(emptyDraft); }}>Cancel</button>
            </div>
          ) : (
            <div className="action-buttons">
              <button
                className="btn-icon"
                data-tooltip={isComplete ? 'Редактировать' : 'Аннотировать'}
                aria-label={isComplete ? 'Редактировать' : 'Аннотировать'}
                onClick={() => startEdit(tx)}
              >✏️</button>
              <button
                className="btn-icon btn-icon--subtle"
                data-tooltip={tx.status === 'paid' ? 'Пометить возвратом' : 'Вернуть в оплаченные'}
                aria-label={tx.status === 'paid' ? 'Пометить возвратом' : 'Вернуть в оплаченные'}
                onClick={() => setPendingStatusTx(tx)}
                disabled={saving}
              >{tx.status === 'paid' ? '↩️' : '↪️'}</button>
            </div>
          )}
        </td>
      </tr>
    );
  };

  const selectedPeriod = period ? periodOptions.find((o) => o.value === period) ?? { value: period, label: periodLabel(period) } : null;

  return (
    <div className="students-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Accounting</h1>
          <p>Квартальный цикл КУДИР: загрузка файлов банка, аннотация транзакций, распределение ПП</p>
        </div>
        <div className="accounting-segments" role="group" aria-label="Раздел">
          <button type="button" className="accounting-segments__btn is-active">Реестр</button>
          <button type="button" className="accounting-segments__btn" onClick={() => navigate('/accounting/stats')}>
            Статистика
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button className="close-error" onClick={() => dispatch(clearError())}>×</button>
        </div>
      )}

      <div className="accounting-uploads">
        <UploadZone
          label="Эквайринг (Reports CSV)"
          hint="Сырой экспорт — импортируются только строки «Завершен»"
          uploading={uploadingAcquiring}
          onFile={handleAcquiringFile}
        />
        <UploadZone
          label="Выписка по счету (Vpsk_Acc CSV)"
          hint="windows-1251 — зачисления выручки и прямые переводы"
          uploading={uploadingStatement}
          onFile={handleStatementFile}
        />
      </div>

      {acquiringSummary && (
        <div className="accounting-summary" data-testid="acquiring-summary">
          <div className="accounting-summary__title">Эквайринг — результат загрузки</div>
          <div className="accounting-summary__counts">
            <span>создано: <strong>{acquiringSummary.created}</strong></span>
            {acquiringSummary.autoLinked > 0 && <span>студент определён по карте: {acquiringSummary.autoLinked}</span>}
            <span>дубликаты: {acquiringSummary.skippedDuplicates}</span>
            {acquiringSummary.skippedDeleted > 0 && <span>удалённые (не восстановлены): {acquiringSummary.skippedDeleted}</span>}
            {Object.entries(acquiringSummary.skippedByStatus).map(([status, count]) => (
              <span key={status}>пропущено «{status}»: {count}</span>
            ))}
          </div>
          {acquiringSummary.refundFlags.length > 0 && (
            <div className="accounting-summary__block accounting-summary__block--danger">
              <div className="accounting-summary__block-title">
                Возврат по ранее импортированному платежу — требуется ручное решение (Mark returned)
              </div>
              <ul>
                {acquiringSummary.refundFlags.map((flag) => (
                  <li key={flag.referenceNumber}>
                    ref {flag.referenceNumber} — {flag.fullName || '—'}, {flag.amount} от {formatDate(flag.paymentDate)}
                    {flag.status === 'returned' ? ' (уже помечен возвратом)' : ` (транзакция #${flag.transactionId})`}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {acquiringSummary.parseErrors.length > 0 && (
            <div className="accounting-summary__block">
              <div className="accounting-summary__block-title">Ошибки разбора</div>
              <ul>
                {acquiringSummary.parseErrors.map((err) => (
                  <li key={`${err.row}-${err.reason}`}>строка {err.row}: {err.reason}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {statementSummary && (
        <div className="accounting-summary" data-testid="statement-summary">
          <div className="accounting-summary__title">Выписка — результат загрузки</div>
          <div className="accounting-summary__counts">
            <span>создано: <strong>{statementSummary.created}</strong></span>
            <span>дубликаты: {statementSummary.skippedDuplicates}</span>
          </div>
          {statementSummary.otherCredits.length > 0 && (
            <div className="accounting-summary__block">
              <div className="accounting-summary__block-title">
                Прямые переводы (не эквайринг) — импортированы по номиналу, проверьте
              </div>
              <ul>
                {statementSummary.otherCredits.map((credit) => (
                  <li key={`${credit.date}-${credit.documentNumber}`}>
                    №{credit.documentNumber} от {formatDate(credit.date)} на {credit.amount} — {credit.description}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {statementSummary.parseErrors.length > 0 && (
            <div className="accounting-summary__block">
              <div className="accounting-summary__block-title">Ошибки разбора</div>
              <ul>
                {statementSummary.parseErrors.map((err) => (
                  <li key={`${err.row}-${err.reason}`}>строка {err.row}: {err.reason}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="filter-bar">
        <div className="accounting-quarter-select">
          <Select<string, SelectOption<string>>
            value={selectedPeriod}
            onChange={(option) => dispatch(setFilters({ quarter: option?.value ?? null }))}
            options={periodOptions}
            placeholder="Квартал / год"
          />
        </div>
        <div className="accounting-segments" role="group" aria-label="Фильтр аннотации">
          {(['all', 'annotated', 'unannotated'] as const).map((value) => (
            <button
              key={value}
              type="button"
              className={`accounting-segments__btn ${annotated === value ? 'is-active' : ''}`}
              onClick={() => dispatch(setFilters({ annotated: value }))}
            >
              {value === 'all' ? 'Все' : value === 'annotated' ? 'Готовые' : 'Не готовые'}
            </button>
          ))}
        </div>
        <div className="accounting-segments" role="group" aria-label="Фильтр распределения">
          {(['all', 'matched', 'unmatched'] as const).map((value) => (
            <button
              key={value}
              type="button"
              className={`accounting-segments__btn ${matched === value ? 'is-active' : ''}`}
              onClick={() => dispatch(setFilters({ matched: value }))}
            >
              {value === 'all' ? 'Все' : value === 'matched' ? 'С ПП' : 'Без ПП'}
            </button>
          ))}
        </div>
      </div>

      <div className="filter-bar">
        <button className="btn-secondary" onClick={handleFillDefaults} disabled={bulkBusy}
          title="Заполнить Г / 25 / (сумма÷25) для всех пустых строк с суммой, кратной 25 (независимо от квартала)">
          Заполнить по умолчанию
        </button>
        <button className="btn-secondary" onClick={() => setShowMatchConfirm(true)} disabled={matching}>
          {matching ? 'Распределение…' : 'Распределить ПП'}
        </button>
        <button className="btn-secondary" onClick={() => setShowAssignActs(true)} disabled={bulkBusy}
          title="Присвоить номера актов подтверждённым строкам без акта (нумерация продолжается автоматически)">
          Присвоить номера актов
        </button>
        <span className="filter-sep filter-sep--divider" aria-hidden="true" />
        <button className="btn-secondary" onClick={() => handleExport('period')} disabled={exporting}
          title="Сырой проверочный CSV по дате платежа (без актов)">
          Скачать CSV{period ? ` (${periodLabel(period)})` : ''}
        </button>
        <button className="btn-secondary" onClick={() => handleExport('year')} disabled={exporting}>
          Скачать год
        </button>
        <button className="btn-secondary" onClick={handleKudirExport} disabled={exporting || !period}
          title={period
            ? (isYearPeriod(period)
              ? `Книга КУДИР за весь ${period} год (по кварталам, .xlsx)`
              : 'Книга КУДИР за выбранный квартал (.xlsx, нужны присвоенные номера актов)')
            : 'Сначала выберите период'}>
          Скачать КУДИР
        </button>
      </div>

      {period && kudirSummary && typeof kudirSummary.count === 'number' && (
        <div className="accounting-kudir-totals" data-testid="kudir-totals">
          <span className="accounting-kudir-totals__scope">КУДИР {periodLabel(period)}</span>
          {kudirSummary.count === 0 ? (
            <span className="accounting-kudir-totals__empty">Актов нет — присвойте номера актов</span>
          ) : (
            <>
              <span className="accounting-kudir-totals__item"><em>Актов</em> {kudirSummary.count}</span>
              <span className="accounting-kudir-totals__item"><em>Выручка</em> {formatMoney(kudirSummary.total)} BYN</span>
              <span className="accounting-kudir-totals__item"><em>Доход 80%</em> {formatMoney(kudirSummary.netIncome)} BYN</span>
              <span className="accounting-kudir-totals__item accounting-kudir-totals__item--tax"><em>Налог 20%</em> {formatMoney(kudirSummary.tax)} BYN</span>
            </>
          )}
        </div>
      )}

      <div className="results-summary">
        Showing {transactions.length} of {pagination.totalItems} transactions
        {bulkNotice && <span className="accounting-bulk-notice"> · {bulkNotice}</span>}
      </div>

      {matchStats && (
        <div className="accounting-match-panel" data-testid="match-panel">
          <div className="accounting-match-panel__title">
            Распределение ПП: {matchStats.docsMatched} из {matchStats.docsTotal} документов,
            {' '}{matchStats.txMatched} транзакций на {matchStats.totalAssignedAmount.toFixed(2)} BYN
          </div>
          {matchStats.unmatchedDocs.length > 0 && (
            <table>
              <thead>
                <tr><th>ПП</th><th>Дата</th><th>Сумма</th><th>Причина</th></tr>
              </thead>
              <tbody>
                {matchStats.unmatchedDocs.map((doc) => (
                  <tr key={`${doc.date}-${doc.documentNumber}`}>
                    <td>№{doc.documentNumber}</td>
                    <td>{formatDate(doc.date)}</td>
                    <td>{doc.amount}</td>
                    <td>
                      {doc.reason}
                      {doc.available != null && ` (доступно ${doc.available})`}
                      {doc.neighborDocumentNumber && ` — сосед №${doc.neighborDocumentNumber}: ${doc.neighborReason}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="students-table-container">
        {loading ? (
          <LoadingSpinner />
        ) : transactions.length === 0 ? (
          <div className="no-students">Нет транзакций — загрузите файл эквайринга или измените фильтры</div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Сумма</th>
                <th>Владелец карты</th>
                <th>Студент</th>
                <th>Формат</th>
                <th>Уроков</th>
                <th>Цена</th>
                <th>ПП</th>
                <th>Статус</th>
                <th>Акт</th>
                <th></th>
              </tr>
            </thead>
            <tbody>{transactions.map(renderRow)}</tbody>
          </table>
        )}
      </div>

      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => dispatch(setPage(pagination.currentPage - 1))}
            disabled={pagination.currentPage === 1 || loading}
            className="btn-secondary"
          >
            Previous
          </button>
          <div className="pagination-info">
            <span>Page {pagination.currentPage} of {pagination.totalPages}</span>
          </div>
          <button
            onClick={() => dispatch(setPage(pagination.currentPage + 1))}
            disabled={pagination.currentPage === pagination.totalPages || loading}
            className="btn-secondary"
          >
            Next
          </button>
        </div>
      )}

      <ConfirmationModal
        isOpen={showAssignActs}
        title="Присвоить номера актов"
        message="Всем готовым (полностью заполненным) строкам без акта — независимо от выбранного квартала — будут присвоены номера актов и рассчитаны периоды услуги, дата акта, сумма прописью и договор оферты. PDF при этом не создаётся — его генерирует локальный сервис позже. Строки, у которых акт уже есть, не затрагиваются."
        confirmText="Присвоить"
        onConfirm={handleAssignActs}
        onCancel={() => { setShowAssignActs(false); setAssignStart(''); }}
      >
        <label className="accounting-assign-start">
          Начальный номер (необязательно — нумерация продолжается автоматически, минимум 10681):
          <input
            type="number"
            min={1}
            className="form-input"
            placeholder="напр. 10681"
            value={assignStart}
            onChange={(e) => setAssignStart(e.target.value)}
          />
        </label>
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={showMatchConfirm}
        title="Распределить ПП"
        message="Матчер пересчитает привязки транзакций к непрораспределённым документам. Уже распределённые документы (галочка distributed) не изменятся. Продолжить?"
        confirmText="Распределить"
        onConfirm={handleRunMatching}
        onCancel={() => setShowMatchConfirm(false)}
      />

      <ConfirmationModal
        isOpen={pendingStatusTx != null}
        title={pendingStatusTx?.status === 'paid' ? 'Пометить возвратом' : 'Вернуть в оплаченные'}
        message={pendingStatusTx?.status === 'paid'
          ? `Транзакция ${pendingStatusTx?.amount} ${pendingStatusTx?.currency} от ${formatDate(pendingStatusTx?.paymentDate)} будет помечена как возврат и исключена из будущего экспорта КУДИР. Строка останется в базе.`
          : `Транзакция ${pendingStatusTx?.amount} ${pendingStatusTx?.currency} от ${formatDate(pendingStatusTx?.paymentDate)} снова будет учитываться как оплаченная.`}
        confirmText="Подтвердить"
        dangerous={pendingStatusTx?.status === 'paid'}
        onConfirm={handleStatusConfirm}
        onCancel={() => setPendingStatusTx(null)}
      />
    </div>
  );
}

export default Accounting;
